import { useState } from 'react';
import type { Customer } from '@/types';

import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from '@univdiam/ui/components/table';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@univdiam/ui/components/card';
import { Button } from '@univdiam/ui/components/button';
import Loader from '@univdiam/ui/customComponents/Loader';
import Drawer from '@univdiam/ui/customComponents/Drawer';
import { format } from 'date-fns';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCustomer } from '@/sharedData/customer';
import api from '@/http/api';
import { AddCustomerForm } from './AddCustomerForm';
import type { AddCustomerFormValues } from './AddCustomerForm';

const createCustomer = (values: AddCustomerFormValues) => {
    return api.post('/admin/customer', {
        full_name: values.full_name,
        email: values.email,
        phone_number: values.phone_number
            ? `${values.phoneNumberCountryCode}${values.phone_number}`
            : undefined,
        business_name: values.business_name,
        business_phone_number: `${values.businessPhoneNumberCountryCode}${values.business_phone_number}`,
        website: values.website,
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2,
        city: values.city,
        state: values.state,
        country: values.country,
        zip_code: values.zip_code,
    });
};

const Customers = () => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [page, setPage] = useState(1);
    const { data, isPending } = useCustomer(page, 20)

    const { mutate, isPending: isCreating, isError, error } = useMutation({
        mutationFn: createCustomer,
        onSuccess: () => {
            toast.success('Customer added successfully!', { position: 'top-center' })
            setDrawerOpen(false)
            queryClient.invalidateQueries({ queryKey: ['customers'] })
        },
    })

    if (isPending) return <Loader />;

    const allCustomers = data?.coreCustomers as Customer[] || data?.data?.coreCustomers as Customer[];
    const customerStaff = data?.staffs || data?.data?.staffs;
    const paginationData = data?.pagination || data?.data?.pagination;
    const sortedCustomers = allCustomers?.sort((a, b) => {
        // First, sort by status (false before true)
        const statusDiff = Number(a.status) - Number(b.status);

        if (statusDiff !== 0) {
            return statusDiff; // If statuses are different, this decides the order
        }

        // If status is the same, sort by createdAt (earliest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    return (
        <div className="mx-auto p-4">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className='text-xl'>Customers Database</CardTitle>
                        <Button onClick={() => setDrawerOpen(true)} className="bg-black hover:bg-gray-800">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Customer
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="whitespace-nowrap">Customer ID</TableHead>
                                    <TableHead className="whitespace-nowrap w-[150px]">Name</TableHead>
                                    <TableHead className="whitespace-nowrap w-[250px]">Email</TableHead>
                                    <TableHead className="whitespace-nowrap">Phone Number</TableHead>
                                    <TableHead className="whitespace-nowrap">Business Name</TableHead>
                                    <TableHead className="whitespace-nowrap">Business Phone</TableHead>
                                    <TableHead className="whitespace-nowrap">Verified</TableHead>
                                    <TableHead className="whitespace-nowrap">Active</TableHead>
                                    <TableHead className="whitespace-nowrap">Location</TableHead>
                                    <TableHead className="whitespace-nowrap">Joined At</TableHead>
                                    <TableHead className="whitespace-nowrap">Staffs</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedCustomers?.map((customer) => {
                                    return (
                                        <TableRow key={customer.customer_id} className='cursor-pointer' onClick={() => navigate(`/customers/${customer.customer_id}`)}>
                                            <TableCell className="whitespace-nowrap">
                                                #{customer.customer_id}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap w-[150px]">
                                                {customer.full_name}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap w-[250px]">
                                                {customer.email}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {customer.phone_number}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {customer.business_name ?? '-'}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {customer.business_phone_number ?? '-'}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${customer.status
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {customer.status ? 'Yes' : 'No'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${customer.is_active_user
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {customer.is_active_user ? 'Yes' : 'No'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap max-w-xs">
                                                <div className="truncate" title={[customer.city, customer.state, customer.country].filter(Boolean).join(', ')}>
                                                    {[customer.city, customer.state, customer.country].filter(Boolean).join(', ')}
                                                </div>
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {customer.created_at ? format(new Date(customer.created_at), 'dd MMM yyyy') : '-'}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                    {customerStaff[customer.customer_id]?.length || 0}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={11}>
                                        <div className="flex items-center justify-between w-full px-2 py-2">
                                            <div className="text-sm font-medium text-muted-foreground">
                                                Total Customers: {paginationData?.totalItems || 0}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                                    disabled={page === 1}
                                                >
                                                    Previous
                                                </Button>
                                                <div className="text-sm font-medium mx-2">
                                                    Page {page} of {paginationData?.totalPages || 1}
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setPage(p => p + 1)}
                                                    disabled={page >= (paginationData?.totalPages || 1)}
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <Drawer
                open={drawerOpen}
                setOpen={setDrawerOpen}
                title="Add Customer"
            >
                <AddCustomerForm
                    onSubmit={(values) => mutate(values)}
                    isLoading={isCreating}
                    isError={isError}
                    error={error as Error | null}
                />
            </Drawer>
        </div>
    );
};

export default Customers;